'use client';

/**
 * @file FilterBar.tsx
 * @description 경매 리스트 필터바 컴포넌트
 *
 * Airbnb 스타일의 필터바로 지역, 매물유형, 정렬을 제공합니다.
 * URL 쿼리 파라미터와 동기화되어 북마크 및 공유가 가능합니다.
 *
 * @see {@link /app/list/page.tsx} - 사용되는 페이지
 */

import { useState, useEffect } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { ChevronDown, Building2, Home, SlidersHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils/shared/core-utils';
import {
  AuctionRegion,
  AuctionPropertyType,
  AuctionSortOrder,
  AuctionFilters,
  AuctionFilterUtils,
  AUCTION_FILTER_OPTIONS,
  AUCTION_FILTER_LABELS,
} from '@/lib/core/types';

/**
 * 접기 상태에서 기본적으로 표시되는 매물 유형들
 */
const DEFAULT_VISIBLE_PROPERTY_TYPES: AuctionPropertyType[] = [
  '아파트',
  '오피스텔',
  '빌라',
  '다세대',
  '주상복합'
];

/**
 * 접기 상태에서 기본적으로 표시되는 지역들
 */
const DEFAULT_VISIBLE_REGIONS: AuctionRegion[] = [
  '서울특별시',
  '경기도',
  '인천광역시'
];

/**
 * 활성화된 지역들 (서울, 경기, 인천만 활성화)
 */
const ENABLED_REGIONS: readonly AuctionRegion[] = ['서울특별시', '경기도', '인천광역시'];

interface FilterBarProps {
  /** 필터 상태 변경 핸들러 */
  onFiltersChange?: (filters: AuctionFilters) => void;
  /** 총 아이템 수 (선택사항) */
  totalCount?: number;
  /** 정렬 옵션 표시 여부 (기본값: true) */
  showSortOptions?: boolean;
}

/**
 * 경매 리스트 필터바 컴포넌트
 *
 * 지역, 매물유형, 정렬 필터를 제공하며 URL과 동기화됩니다.
 */
export function FilterBar({ onFiltersChange, totalCount, showSortOptions = true }: FilterBarProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  
  // URL 파라미터에서 필터 상태 읽기
  const regions = AuctionFilterUtils.paramToRegions(searchParams.get('regions'));
  const propertyTypes = AuctionFilterUtils.paramToTypes(searchParams.get('types'));
  const sortOrder = AuctionFilterUtils.paramToSort(searchParams.get('sort'));
  
  // URL 파라미터에서 expanded 상태 읽기 (기본값: false - 접힌 상태)
  const [isExpanded, setIsExpanded] = useState(() => {
    // URL에 명시적으로 expanded=true가 있을 때만 true
    return searchParams.get('expanded') === 'true';
  });

  // URL 파라미터 변경 시 expanded 상태 동기화
  useEffect(() => {
    const expanded = searchParams.get('expanded') === 'true';
    setIsExpanded(expanded);
  }, [searchParams]);

  const currentFilters: AuctionFilters = {
    regions,
    propertyTypes,
    sortOrder,
  };

  /**
   * URL 파라미터 업데이트 함수
   */
  const updateSearchParams = (newFilters: Partial<AuctionFilters>) => {
    const params = new URLSearchParams(searchParams.toString());

    if (newFilters.regions !== undefined) {
      const regionsParam = AuctionFilterUtils.regionsToParam(newFilters.regions);
      if (regionsParam) {
        params.set('regions', regionsParam);
      } else {
        params.delete('regions');
      }
    }

    if (newFilters.propertyTypes !== undefined) {
      const typesParam = AuctionFilterUtils.typesToParam(newFilters.propertyTypes);
      if (typesParam) {
        params.set('types', typesParam);
      } else {
        params.delete('types');
      }
    }

    if (newFilters.sortOrder !== undefined) {
      params.set('sort', AuctionFilterUtils.sortToParam(newFilters.sortOrder));
    }

    router.replace(`?${params.toString()}`, { scroll: false });
    onFiltersChange?.({ ...currentFilters, ...newFilters });
  };

  /**
   * 지역 필터 토글
   */
  const toggleRegion = (region: AuctionRegion) => {
    const isEnabled = ENABLED_REGIONS.includes(region);
    if (!isEnabled) {
      console.log(`[FilterBar] Disabled region selected: ${region}`);
      return;
    }

    console.log(`[FilterBar] Toggling region: ${region}`);
    const newRegions = regions.includes(region)
      ? regions.filter(r => r !== region)
      : [...regions, region];
    updateSearchParams({ regions: newRegions });
  };

  /**
   * 매물 유형 필터 토글
   */
  const togglePropertyType = (type: AuctionPropertyType) => {
    const newTypes = propertyTypes.includes(type)
      ? propertyTypes.filter(t => t !== type)
      : [...propertyTypes, type];
    updateSearchParams({ propertyTypes: newTypes });
  };

  /**
   * 정렬 순서 변경
   */
  const changeSortOrder = (sort: AuctionSortOrder) => {
    updateSearchParams({ sortOrder: sort });
  };

  /**
   * 매물 유형별 아이콘 반환
   */
  const getPropertyTypeIcon = (type: AuctionPropertyType) => {
    switch (type) {
      case '아파트':
      case '오피스텔':
      case '주상복합':
        return Building2;
      case '빌라':
      case '다세대':
        return Home;
      default:
        return Building2;
    }
  };

  return (
    <div className="border-t border-zinc-100 dark:border-zinc-700 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-md">
      <div className="max-w-screen-xl mx-auto px-3 sm:px-4 md:px-6">
        {/* 기본 필터 영역 */}
        <div className="py-3 md:py-4">
          {/* 모바일: 세로 레이아웃, 데스크톱: 가로 레이아웃 */}
          <div className="flex flex-col md:flex-row md:items-center md:gap-3">
            {/* 스크롤 가능한 필터 영역 */}
            <div className="flex-1 flex items-center gap-2 md:gap-3 overflow-x-auto scrollbar-hide pb-2 md:pb-0 -mx-3 px-3 md:mx-0 md:px-0">
              {/* 지역 그룹 */}
              <div className="flex items-center gap-1.5 sm:gap-2 pr-2 sm:pr-4 border-r border-zinc-200 dark:border-zinc-700 shrink-0">
                {AUCTION_FILTER_OPTIONS.regions
                  .filter(region => DEFAULT_VISIBLE_REGIONS.includes(region))
                  .map((region) => {
                    const isEnabled = ENABLED_REGIONS.includes(region);
                    return (
                      <button
                        key={region}
                        onClick={() => isEnabled && toggleRegion(region)}
                        disabled={!isEnabled}
                        className={cn(
                          'px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-full border text-xs sm:text-sm font-bold transition-all whitespace-nowrap shrink-0',
                          regions.includes(region) && isEnabled
                            ? 'bg-black dark:bg-zinc-100 text-white dark:text-zinc-900 border-black dark:border-zinc-100'
                            : isEnabled
                            ? 'border-zinc-200 dark:border-zinc-600 text-zinc-600 dark:text-zinc-300 hover:border-zinc-800 dark:hover:border-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                            : 'border-zinc-200 dark:border-zinc-600 text-zinc-400 dark:text-zinc-500 cursor-not-allowed opacity-50'
                        )}
                      >
                        {AUCTION_FILTER_LABELS.regions[region]}
                      </button>
                    );
                  })}
              </div>

              {/* 매물 유형 필터 - 기본적으로 표시 */}
              <div className="flex gap-1.5 sm:gap-2 md:gap-3 pl-2 sm:pl-4 shrink-0">
                {AUCTION_FILTER_OPTIONS.propertyTypes
                  .filter(type => DEFAULT_VISIBLE_PROPERTY_TYPES.includes(type))
                  .map((type) => {
                  const Icon = getPropertyTypeIcon(type);
                  return (
                    <button
                      key={type}
                      onClick={() => togglePropertyType(type)}
                      className={cn(
                        'flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-full border text-xs sm:text-sm font-medium transition-all whitespace-nowrap shrink-0',
                        propertyTypes.includes(type)
                          ? 'bg-black dark:bg-zinc-100 text-white dark:text-zinc-900 border-black dark:border-zinc-100'
                          : 'border-zinc-200 dark:border-zinc-600 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                      )}
                    >
                      <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">{type}</span>
                      <span className="sm:hidden">{type.slice(0, 2)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 우측 영역: 정렬 옵션 + 필터 버튼 */}
            <div className="flex items-center justify-between md:justify-end gap-2 md:gap-3 shrink-0 mt-2 md:mt-0 pt-2 md:pt-0 border-t md:border-t-0 border-zinc-200 dark:border-zinc-700 md:ml-auto">
              {/* 기본 정렬 옵션 - showSortOptions가 true일 때만 표시 */}
              {showSortOptions && (
                <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto scrollbar-hide -mx-3 px-3 md:mx-0 md:px-0">
                  {AUCTION_FILTER_OPTIONS.sortOrders.map((sort) => (
                    <button
                      key={sort}
                      onClick={() => changeSortOrder(sort)}
                      className={cn(
                        'px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-bold transition whitespace-nowrap shrink-0',
                        sortOrder === sort
                          ? 'border-b-2 border-black dark:border-zinc-100 text-black dark:text-zinc-100'
                          : 'text-zinc-500 dark:text-zinc-400 hover:text-black dark:hover:text-zinc-100'
                      )}
                    >
                      {sort}
                    </button>
                  ))}
                </div>
              )}

              {/* 필터 버튼 - 우측 끝 */}
              <button
                onClick={() => {
                  const newExpanded = !isExpanded;
                  setIsExpanded(newExpanded);
                  // URL 파라미터 업데이트 (접힌 상태일 때는 파라미터 제거)
                  const params = new URLSearchParams(searchParams.toString());
                  if (newExpanded) {
                    params.set('expanded', 'true');
                  } else {
                    // 접힌 상태로 변경 시 expanded 파라미터 제거
                    params.delete('expanded');
                  }
                  router.replace(`?${params.toString()}`, { scroll: false });
                }}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 border border-zinc-200 dark:border-zinc-600 rounded-full text-xs sm:text-sm font-medium text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition whitespace-nowrap shrink-0"
              >
                <SlidersHorizontal className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">필터</span>
                <ChevronDown className={cn(
                  'w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform',
                  isExpanded && 'rotate-180'
                )} />
              </button>
            </div>
          </div>
        </div>

        {/* 확장된 필터 영역 - 추가 필터 옵션들 */}
        {isExpanded && (
          <div className="pb-4 border-t border-zinc-100 dark:border-zinc-700 pt-4">
            {/* 추가 지역역 필터 */}
            <div className="mb-4">
              <h4 className="text-xs sm:text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2 sm:mb-3">추가 지역</h4>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {AUCTION_FILTER_OPTIONS.regions
                  .filter(region => !DEFAULT_VISIBLE_REGIONS.includes(region))
                  .map((region) => {
                    const isEnabled = ENABLED_REGIONS.includes(region);
                    return (
                      <button
                        key={region}
                        onClick={() => isEnabled && toggleRegion(region)}
                        disabled={!isEnabled}
                        className={cn(
                          'px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-full border text-xs sm:text-sm font-bold transition-all whitespace-nowrap',
                          regions.includes(region) && isEnabled
                            ? 'bg-black dark:bg-zinc-100 text-white dark:text-zinc-900 border-black dark:border-zinc-100'
                            : isEnabled
                            ? 'border-zinc-200 dark:border-zinc-600 text-zinc-600 dark:text-zinc-300 hover:border-zinc-800 dark:hover:border-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                            : 'border-zinc-200 dark:border-zinc-600 text-zinc-400 dark:text-zinc-500 cursor-not-allowed opacity-50'
                        )}
                      >
                        {AUCTION_FILTER_LABELS.regions[region]}
                      </button>
                    );
                  })}
              </div>
            </div>

            {/* 필터 요약 */}
            {(regions.length > 0 || propertyTypes.length > 0) && (
              <div className="mt-3 text-xs sm:text-sm text-zinc-500 dark:text-zinc-400 break-words">
                {regions.length > 0 && (
                  <span>지역: {regions.map(r => AUCTION_FILTER_LABELS.regions[r]).join(', ')}</span>
                )}
                {regions.length > 0 && propertyTypes.length > 0 && <span className="mx-1">·</span>}
                {propertyTypes.length > 0 && (
                  <span>유형: {propertyTypes.map(t => AUCTION_FILTER_LABELS.propertyTypes[t]).join(', ')}</span>
                )}
                {totalCount !== undefined && (
                  <span className="ml-1 sm:ml-2 text-zinc-400 dark:text-zinc-500">
                    (총 {totalCount.toLocaleString()}건)
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
